import {
  Container,
  Box,
  Flex,
  Text,
  VStack,
  Badge,
  Icon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Megaphone, Calendar, Recycle, Truck, Leaf, Sparkles, Wind } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { Copyright, BuyCoffeeButton } from "../../components";
import { ReactNode } from "react";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";

// Typ dla aktualności
type NewsItem = {
  id: number;
  title: string;
  date: string;
  content: ReactNode;
  type: "info" | "warning" | "update" | "eco";
};

// Przykładowe aktualności - można potem zastąpić danymi z API
const newsData: NewsItem[] = [
  {
    id: 4,
    title: "Motyw ciemny i nowe ustawienia",
    date: "2025-12-31",
    content: (
      <>
        🌙 Dodałem <strong>motyw ciemny</strong> i całkowicie przebudowałem sekcję ustawień!
        <br /><br />
        <strong>Nowe funkcje:</strong>
        <ul style={{ marginTop: "0.5rem", marginBottom: "0.5rem", paddingLeft: "1.5rem" }}>
          <li><strong>Motyw ciemny</strong> – wybierz preferowany motyw kolorystyczny aplikacji (jasny lub ciemny)</li>
          <li><strong>Przebudowane ustawienia</strong> – nowy, bardziej przejrzysty interfejs ustawień z lepszą organizacją opcji</li>
          <li><strong>Przełączanie motywu</strong> – łatwe przełączanie między motywem jasnym a ciemnym w każdej chwili</li>
        </ul>
        Motyw ciemny jest idealny do korzystania z aplikacji wieczorem lub w słabo oświetlonych pomieszczeniach. 
        Wszystkie ustawienia są teraz bardziej intuicyjne i łatwo dostępne!
        <br />
        <br />
        Jeśli doceniasz moją pracę i chcesz wesprzeć rozwój aplikacji – 
        <strong>postaw mi wirtualną kawę ☕️</strong>. To daje mi motywację do dalszych ulepszeń!
        <br />
        <br />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BuyCoffeeButton />
        </div>
      </>
    ),
    type: "update",
  },
  {
    id: 3,
    title: "Nowe widgety: słońce i postęp roku",
    date: "2025-12-30",
    content: (
      <>
        ☀️ Dodałem trzy nowe widgety, które możesz teraz dodać do swojej strony głównej!
        <br /><br />
        <strong>Nowe widgety:</strong>
        <ul style={{ marginTop: "0.5rem", marginBottom: "0.5rem", paddingLeft: "1.5rem" }}>
          <li><strong>Wschód i zachód słońca</strong> – sprawdź dokładne godziny wschodu i zachodu słońca na dziś</li>
          <li><strong>Długość dnia</strong> – zobacz ile godzin trwa dzień (różnica między zachodem a wschodem)</li>
          <li><strong>Postęp roku</strong> – śledź ile procent roku już minęło z animowanym paskiem postępu</li>
        </ul>
        Wszystkie widgety są dostępne w sekcji widgetów na stronie głównej. Kliknij przycisk <strong>"Dodaj widget"</strong>, 
        aby wybrać które widgety chcesz wyświetlać. Możesz dodać maksymalnie 6 widgetów jednocześnie!
        <br />
        <br />
        Jeśli doceniasz moją pracę i chcesz wesprzeć rozwój aplikacji – 
        <strong>postaw mi wirtualną kawę ☕️</strong>. To daje mi motywację do dalszych ulepszeń!
        <br />
        <br />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BuyCoffeeButton />
        </div>
      </>
    ),
    type: "update",
  },
  {
    id: 3,
    title: "Harmonogram wywozu na 2026 rok",
    date: "2025-12-30",
    content: (
      <>
        📅 Harmonogram wywozu odpadów na <strong>2026 rok</strong> pojawi się automatycznie w aplikacji!
        <br /><br />
        Aplikacja automatycznie przełączy się na nowy harmonogram w 2026 roku. 
        Jeśli lista harmonogramu na bieżący rok jest już pusta, możesz ręcznie wybrać <strong>rok 2026</strong> 
        z dostęopnej listy lat.
        <br /><br />
        Dzięki temu zawsze będziesz mieć dostęp do aktualnego harmonogramu wywozu odpadów!
        <br />
        <br />
        Jeśli doceniasz moją pracę i chcesz wesprzeć rozwój aplikacji – 
        <strong>postaw mi wirtualną kawę ☕️</strong>. To daje mi motywację do dalszych ulepszeń!
        <br />
        <br />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BuyCoffeeButton />
        </div>
      </>
    ),
    type: "update",
  },
  {
    id: 2,
    title: "Nowa zakładka: Jakość powietrza w gminie",
    date: "2025-12-23",
    content: (
      <>
        🌬️ Dodałem nową zakładkę <strong>"Powietrze"</strong>, która pokazuje aktualną jakość powietrza w gminie!
        <br /><br />
        Możesz teraz sprawdzić:
        <ul style={{ marginTop: "0.5rem", marginBottom: "0.5rem", paddingLeft: "1.5rem" }}>
          <li>Wskaźnik AQI (Air Quality Index)</li>
          <li>Poziomy PM2.5 i PM10</li>
          <li>Warunki atmosferyczne (temperatura, wilgotność, ciśnienie)</li>
          <li>Dane z najbliższej stacji pomiarowej</li>
        </ul>
        Dane są aktualizowane automatycznie i pochodzą z systemu monitoringu jakości powietrza OSE. 
        Możesz wybrać lokalizację z listy dostępnych punktów pomiarowych w gminie.
        <br />
        <br />
        Jeśli doceniasz moją pracę i chcesz wesprzeć rozwój aplikacji – 
        <strong>postaw mi wirtualną kawę ☕️</strong>. To daje mi motywację do dalszych ulepszeń!
        <br />
        <br />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BuyCoffeeButton />
        </div>
      </>
    ),
    type: "eco",
  },
  {
    id: 1,
    title: "Nowy rozdział: aplikacja gotowa na 2026!",
    date: "2025-12-22",
    content: (
      <>
        🚀 Udało się! Właśnie wdrożyłem nową wersję aplikacji – dopieszczoną, stabilną i gotową, by towarzyszyć Ci przez cały 2026 rok!  
        <br /><br />
        Każda aktualizacja to dziesiątki godzin pracy, testów i szlifowania detali. Jeśli doceniasz moją pasję i chcesz wesprzeć rozwój projektu – 
        <strong>postaw mi wirtualną kawę ☕️</strong>. Daje mi to ogromną motywację, by ciągle ulepszać aplikację!  
        <br />
        <br />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BuyCoffeeButton />
        </div>
      </>
    ),
    type: "info",
  }
];

const getIconForType = (type: NewsItem["type"], customIcon?: any) => {
  if (customIcon) return customIcon;
  switch (type) {
    case "warning":
      return Megaphone;
    case "update":
      return Recycle;
    case "eco":
      return Leaf;
    default:
      return Truck;
  }
};

const getBadgeColor = (type: NewsItem["type"], theme: AppTheme) => {
  switch (type) {
    case "warning":
      return theme.accent;
    case "update":
      return theme.brand;
    case "eco":
      return theme.success;
    default:
      return theme.primary;
  }
};

const getBadgeLabel = (type: NewsItem["type"]) => {
  switch (type) {
    case "warning":
      return "Ważne";
    case "update":
      return "Aktualizacja";
    case "eco":
      return "Eko-porady";
    default:
      return "Informacja";
  }
};

const HeaderContainer = styled.header<{ $theme: AppTheme }>`
  background: ${({ $theme }) => $theme.gradientHeader};
  padding: 2.5rem 1rem;
  position: relative;
  overflow: hidden;
`;

const HeaderDecoration = styled.div<{ $theme: AppTheme }>`
  position: absolute;
  top: -60%;
  right: -25%;
  width: 350px;
  height: 350px;
  border-radius: 50%;
  background: ${({ $theme }) => `${$theme.primary}15`};
  filter: blur(40px);
`;

const HeaderDecorationSmall = styled.div<{ $theme: AppTheme }>`
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: ${({ $theme }) => `${$theme.accent}10`};
  filter: blur(30px);
`;

const MotionBox = motion(Box);

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const NewsCard = ({ item, index, theme }: { item: NewsItem; index: number; theme: AppTheme }) => {
  // Dla wiadomości o jakości powietrza użyj ikony Wind
  const customIcon = item.id === 2 ? Wind : undefined;
  const IconComponent = getIconForType(item.type, customIcon);
  const badgeColor = getBadgeColor(item.type, theme);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.4, 0, 0.2, 1] }}
      bg={theme.backgroundCard}
      borderRadius={THEME.borderRadiusLg}
      boxShadow={theme.shadowMd}
      border={`1px solid ${theme.borderCard}`}
      overflow="hidden"
      position="relative"
      _hover={{
        boxShadow: theme.shadowLg,
        transform: "translateY(-4px)",
        borderColor: `${theme.primary}40`,
      }}
      style={{ transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" }}
    >
      {/* Decorative corner gradient */}
      <Box
        position="absolute"
        top={0}
        right={0}
        width="150px"
        height="150px"
        background={`radial-gradient(circle at 100% 0%, ${badgeColor}15, transparent 70%)`}
        pointerEvents="none"
      />
      
      <Box p={6}>
        <Flex justify="space-between" align="flex-start" mb={4} flexWrap="wrap" gap={3}>
          <Flex align="center" gap={3}>
            <Box
              p={3}
              borderRadius={THEME.borderRadius}
              bg={`${badgeColor}15`}
              color={badgeColor}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={IconComponent} boxSize={5} />
            </Box>
            <Badge
              bg={badgeColor}
              color="white"
              px={3}
              py={1.5}
              borderRadius="full"
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.05em"
              boxShadow={`0 2px 10px ${badgeColor}40`}
            >
              {getBadgeLabel(item.type)}
            </Badge>
          </Flex>
          <Flex align="center" gap={2} color={theme.textMuted} fontSize="sm" fontWeight="500">
            <Icon as={Calendar} boxSize={4} />
            <Text>{formatDate(item.date)}</Text>
          </Flex>
        </Flex>

        <Text
          fontFamily="'Outfit', sans-serif"
          fontSize="xl"
          fontWeight="700"
          color={theme.text}
          mb={3}
          lineHeight="short"
          letterSpacing="-0.02em"
        >
          {item.title}
        </Text>

        <Text 
          color={theme.textSecondary} 
          fontSize="sm" 
          lineHeight="tall"
          fontWeight="500"
        >
          {item.content}
        </Text>
      </Box>
    </MotionBox>
  );
};

export function NewsPage() {
  const theme = useTheme() as AppTheme;
  
  return (
    <Box minH="100vh" bg={theme.background}>
      <HeaderContainer $theme={theme}>
        <HeaderDecoration $theme={theme} />
        <HeaderDecorationSmall $theme={theme} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Flex align="center" gap={3} mb={3}>
              <Box
                p={3}
                borderRadius={THEME.borderRadius}
                bg="rgba(255,255,255,0.15)"
                backdropFilter="blur(10px)"
                border="1px solid rgba(255,255,255,0.2)"
              >
                <Icon as={Megaphone} boxSize={6} color="white" />
              </Box>
              <Text
                fontFamily="'Outfit', sans-serif"
                fontSize="2xl"
                fontWeight="800"
                color="white"
                letterSpacing="-0.02em"
              >
                Aktualności
              </Text>
              <Icon as={Sparkles} boxSize={5} color="white" opacity={0.7} />
            </Flex>
            <Text color="whiteAlpha.800" fontSize="sm" fontWeight="500" pl={1}>
              Najnowsze informacje i ogłoszenia
            </Text>
          </MotionBox>
        </Container>
      </HeaderContainer>

      <Container maxW="container.md" py={8} px={4}>
        <VStack spacing={5} align="stretch">
          {newsData.map((item, index) => (
            <NewsCard key={item.id} item={item} index={index} theme={theme} />
          ))}
        </VStack>

        <Box mt={10}>
          <Copyright />
        </Box>
      </Container>
    </Box>
  );
}
